/**
 * @file Briques de rendu partagees par les gros FX (FX de tir, auras « orbites ») : un atlas de formes dessine en code (aucun asset), un lot de quads
 * texturés (billboards, quads orientes le long d'une vitesse, quads a plat sur le sol) et un lot de rubans (traines, eclairs) — chacun = UN draw call,
 * reconstruit a chaque frame dans des tampons pre-alloues (aucune allocation par frame). Melange normal (les decors sont clairs).
 */
import { BufferAttribute, BufferGeometry, CanvasTexture, DynamicDrawUsage, DoubleSide, LinearFilter, Mesh, MeshBasicMaterial } from 'three';
import type { Scene, Texture, Vector3 } from 'three';

/** Cellules de l'atlas (8 colonnes x 4 lignes de 128 px). */
export enum Cell {
  Glow,
  Disc,
  Star,
  Diamond,
  Heart,
  Spark,
  Ring,
  Square,
  Flame,
  Petal,
  Bubble,
  Flake,
  Note,
  Blade,
  Triangle,
  Plus,
  RuneCircle,
  Mandala,
  StarRune,
  Checker,
  Lotus,
  Vortex,
  Rings,
  Pulse,
  Smoke,
  Streak,
  Shard,
  Crescent,
}

const COLS = 8;
const ROWS = 4;
const CELL_PX = 128;
const TAU = Math.PI * 2;

const draw = (cell: Cell, g: CanvasRenderingContext2D): void => {
  const c = CELL_PX / 2;
  g.save();
  g.translate(c, c);
  g.fillStyle = '#fff';
  g.strokeStyle = '#fff';
  g.lineJoin = 'round';
  g.lineCap = 'round';
  const poly = (pts: Array<[number, number]>): void => {
    g.beginPath();
    pts.forEach(([x, y], i) => (i === 0 ? g.moveTo(x, y) : g.lineTo(x, y)));
    g.closePath();
    g.fill();
  };
  const radial = (r: number, stops: Array<[number, number]>): void => {
    const gr = g.createRadialGradient(0, 0, 0, 0, 0, r);
    for (const [o, a] of stops) gr.addColorStop(o, `rgba(255,255,255,${a})`);
    g.fillStyle = gr;
    g.beginPath();
    g.arc(0, 0, r, 0, TAU);
    g.fill();
  };
  switch (cell) {
    case Cell.Glow:
      radial(60, [[0, 1], [0.25, 0.7], [0.6, 0.22], [1, 0]]);
      break;
    case Cell.Disc:
      radial(58, [[0, 1], [0.78, 1], [0.92, 0.6], [1, 0]]);
      break;
    case Cell.Star: {
      const pts: Array<[number, number]> = [];
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * TAU) / 10;
        const r = i % 2 === 0 ? 58 : 25;
        pts.push([Math.cos(a) * r, Math.sin(a) * r]);
      }
      poly(pts);
      break;
    }
    case Cell.Diamond:
      poly([[0, -58], [34, 0], [0, 58], [-34, 0]]);
      break;
    case Cell.Heart:
      g.beginPath();
      g.moveTo(0, 46);
      g.bezierCurveTo(-70, -2, -42, -58, 0, -26);
      g.bezierCurveTo(42, -58, 70, -2, 0, 46);
      g.fill();
      break;
    case Cell.Spark: {
      // flare a 4 branches : deux losanges fins croises + coeur lumineux
      poly([[0, -60], [7, 0], [0, 60], [-7, 0]]);
      poly([[-60, 0], [0, 7], [60, 0], [0, -7]]);
      radial(22, [[0, 1], [1, 0]]);
      break;
    }
    case Cell.Ring:
      g.lineWidth = 10;
      g.beginPath();
      g.arc(0, 0, 52, 0, TAU);
      g.stroke();
      break;
    case Cell.Square:
      g.fillRect(-42, -42, 84, 84);
      break;
    case Cell.Flame:
      g.beginPath();
      g.moveTo(0, -60);
      g.bezierCurveTo(46, -10, 44, 52, 0, 56);
      g.bezierCurveTo(-44, 52, -46, -10, 0, -60);
      g.fill();
      break;
    case Cell.Petal:
      g.beginPath();
      g.moveTo(0, -58);
      g.bezierCurveTo(52, -26, 40, 40, 0, 56);
      g.bezierCurveTo(-40, 40, -52, -26, 0, -58);
      g.fill();
      break;
    case Cell.Bubble:
      g.lineWidth = 7;
      g.beginPath();
      g.arc(0, 0, 50, 0, TAU);
      g.stroke();
      g.globalAlpha = 0.45;
      g.beginPath();
      g.arc(0, 0, 47, 0, TAU);
      g.fill();
      g.globalAlpha = 1;
      g.beginPath();
      g.arc(-18, -20, 10, 0, TAU);
      g.fill();
      break;
    case Cell.Flake:
      g.lineWidth = 9;
      for (let i = 0; i < 3; i++) {
        g.save();
        g.rotate((i * Math.PI) / 3);
        g.beginPath();
        g.moveTo(0, -56);
        g.lineTo(0, 56);
        g.stroke();
        g.restore();
      }
      break;
    case Cell.Note:
      g.beginPath();
      g.ellipse(-16, 40, 22, 16, -0.4, 0, TAU);
      g.fill();
      g.lineWidth = 9;
      g.beginPath();
      g.moveTo(2, 36);
      g.lineTo(2, -46);
      g.lineTo(34, -26);
      g.stroke();
      break;
    case Cell.Blade:
      // croissant effile
      g.beginPath();
      g.moveTo(-56, 0);
      g.quadraticCurveTo(0, -64, 56, 0);
      g.quadraticCurveTo(0, -24, -56, 0);
      g.fill();
      break;
    case Cell.Triangle:
      poly([[0, -60], [40, 52], [-40, 52]]);
      break;
    case Cell.Plus:
      g.fillRect(-12, -52, 24, 104);
      g.fillRect(-52, -12, 104, 24);
      break;
    case Cell.RuneCircle:
      g.lineWidth = 5;
      for (const r of [58, 46, 30]) {
        g.beginPath();
        g.arc(0, 0, r, 0, TAU);
        g.stroke();
      }
      for (let i = 0; i < 12; i++) {
        g.save();
        g.rotate((i * TAU) / 12);
        g.fillRect(-2, -58, 4, 12);
        g.restore();
      }
      g.lineWidth = 4;
      g.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * TAU) / 6 - Math.PI / 2;
        if (i === 0) g.moveTo(Math.cos(a) * 30, Math.sin(a) * 30);
        else g.lineTo(Math.cos(a) * 30, Math.sin(a) * 30);
      }
      g.closePath();
      g.stroke();
      break;
    case Cell.Mandala: {
      for (let layer = 0; layer < 3; layer++) {
        const n = 8 + layer * 4;
        const r0 = 14 + layer * 16;
        for (let i = 0; i < n; i++) {
          g.save();
          g.rotate((i * TAU) / n + layer * 0.2);
          g.beginPath();
          g.moveTo(0, -r0);
          g.quadraticCurveTo(10 - layer, -r0 - 16, 0, -r0 - 24 - layer * 3);
          g.quadraticCurveTo(-10 + layer, -r0 - 16, 0, -r0);
          g.fill();
          g.restore();
        }
      }
      g.lineWidth = 4;
      g.beginPath();
      g.arc(0, 0, 60, 0, TAU);
      g.stroke();
      break;
    }
    case Cell.StarRune: {
      g.lineWidth = 6;
      g.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = -Math.PI / 2 + (i * 2 * TAU) / 5;
        if (i === 0) g.moveTo(Math.cos(a) * 54, Math.sin(a) * 54);
        else g.lineTo(Math.cos(a) * 54, Math.sin(a) * 54);
      }
      g.closePath();
      g.stroke();
      g.beginPath();
      g.arc(0, 0, 60, 0, TAU);
      g.stroke();
      break;
    }
    case Cell.Checker: {
      g.lineWidth = 4;
      g.beginPath();
      g.arc(0, 0, 60, 0, TAU);
      g.stroke();
      const s = 18;
      for (let y = -3; y < 3; y++) {
        for (let x = -3; x < 3; x++) {
          if ((x + y) & 1 && Math.hypot((x + 0.5) * s, (y + 0.5) * s) < 56) g.fillRect(x * s, y * s, s, s);
        }
      }
      break;
    }
    case Cell.Lotus:
      for (let i = 0; i < 8; i++) {
        g.save();
        g.rotate((i * TAU) / 8);
        g.beginPath();
        g.moveTo(0, -8);
        g.quadraticCurveTo(26, -34, 0, -60);
        g.quadraticCurveTo(-26, -34, 0, -8);
        g.fill();
        g.restore();
      }
      g.beginPath();
      g.arc(0, 0, 9, 0, TAU);
      g.fill();
      break;
    case Cell.Vortex:
      g.lineWidth = 9;
      for (let arm = 0; arm < 3; arm++) {
        g.beginPath();
        for (let i = 0; i <= 28; i++) {
          const t = i / 28;
          const a = arm * (TAU / 3) + t * 3.2;
          const r = 8 + t * 50;
          if (i === 0) g.moveTo(Math.cos(a) * r, Math.sin(a) * r);
          else g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        }
        g.stroke();
      }
      break;
    case Cell.Rings:
      g.lineWidth = 6;
      for (const r of [58, 44, 30, 16]) {
        g.globalAlpha = r / 58;
        g.beginPath();
        g.arc(0, 0, r, 0, TAU);
        g.stroke();
      }
      break;
    case Cell.Pulse: {
      const gr = g.createRadialGradient(0, 0, 30, 0, 0, 60);
      gr.addColorStop(0, 'rgba(255,255,255,0)');
      gr.addColorStop(0.7, 'rgba(255,255,255,0.9)');
      gr.addColorStop(0.9, 'rgba(255,255,255,0.4)');
      gr.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = gr;
      g.beginPath();
      g.arc(0, 0, 60, 0, TAU);
      g.fill();
      break;
    }
    case Cell.Smoke:
      radial(60, [[0, 0.9], [0.5, 0.65], [0.85, 0.25], [1, 0]]);
      break;
    case Cell.Streak: {
      const gr = g.createLinearGradient(-60, 0, 60, 0);
      gr.addColorStop(0, 'rgba(255,255,255,0)');
      gr.addColorStop(0.55, 'rgba(255,255,255,0.7)');
      gr.addColorStop(1, 'rgba(255,255,255,1)');
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(-60, 0);
      g.quadraticCurveTo(0, -16, 60, -5);
      g.lineTo(60, 5);
      g.quadraticCurveTo(0, 16, -60, 0);
      g.fill();
      break;
    }
    case Cell.Shard:
      // cerf-volant oriente vers +x (la pointe est la tete quand le quad est etire le long d'une vitesse)
      poly([[60, 0], [-6, -24], [-60, 0], [-6, 24]]);
      g.globalCompositeOperation = 'destination-out';
      g.globalAlpha = 0.35;
      poly([[60, 0], [-6, -24], [-2, 0]]);
      break;
    case Cell.Crescent:
      g.beginPath();
      g.arc(0, 0, 54, 0, TAU);
      g.fill();
      g.globalCompositeOperation = 'destination-out';
      g.beginPath();
      g.arc(16, -8, 46, 0, TAU);
      g.fill();
      break;
  }
  g.restore();
};

/** Construit la texture d'atlas (a appeler une seule fois par renderer). */
export const buildAtlas = (): Texture => {
  const canvas = document.createElement('canvas');
  canvas.width = COLS * CELL_PX;
  canvas.height = ROWS * CELL_PX;
  const g = canvas.getContext('2d') as CanvasRenderingContext2D;
  const total = COLS * ROWS;
  for (let i = 0; i < total && Cell[i] !== undefined; i++) {
    g.save();
    g.translate((i % COLS) * CELL_PX, Math.floor(i / COLS) * CELL_PX);
    g.beginPath();
    g.rect(0, 0, CELL_PX, CELL_PX);
    g.clip();
    draw(i as Cell, g);
    g.restore();
  }
  const tex = new CanvasTexture(canvas);
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = false;
  return tex;
};

/** HSV (h en tours) -> rgb dans `out[0..2]`. */
export const hsv = (h: number, s: number, v: number, out: number[] | Float32Array, o = 0): void => {
  const hh = (h - Math.floor(h)) * 6;
  const i = Math.floor(hh);
  const f = hh - i;
  const p = v * (1 - s);
  const q = v * (1 - s * f);
  const t = v * (1 - s * (1 - f));
  let r = v;
  let g = t;
  let b = p;
  switch (i % 6) {
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }
  out[o] = r;
  out[o + 1] = g;
  out[o + 2] = b;
};

/** `#rrggbb` -> [r, g, b] (0..1). */
export const hexRgb = (hex: string): [number, number, number] => {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

/** Vecteurs de la camera de la frame (droite, haut, position, direction de visee). */
export interface CamBasis {
  right: Vector3;
  up: Vector3;
  pos: Vector3;
}

/**
 * Lot de quads texturés (atlas) : `begin` ... `quad*` ... `end`. Les couleurs sont multipliees par la texture (blanche) ; l'alpha de sommet = opacite.
 */
export class SpriteBatch {
  readonly mesh: Mesh;
  private readonly pos: Float32Array;
  private readonly uv: Float32Array;
  private readonly col: Float32Array;
  private readonly posAttr: BufferAttribute;
  private readonly uvAttr: BufferAttribute;
  private readonly colAttr: BufferAttribute;
  private n = 0;
  readonly material: MeshBasicMaterial;

  constructor(
    private readonly scene: Scene,
    atlas: Texture,
    readonly capacity: number,
    renderOrder = 4,
  ) {
    this.pos = new Float32Array(capacity * 12);
    this.uv = new Float32Array(capacity * 8);
    this.col = new Float32Array(capacity * 16);
    const index = new Uint16Array(capacity * 6);
    for (let i = 0; i < capacity; i++) {
      const v = i * 4;
      index.set([v, v + 1, v + 2, v + 2, v + 1, v + 3], i * 6);
    }
    const geometry = new BufferGeometry();
    this.posAttr = new BufferAttribute(this.pos, 3).setUsage(DynamicDrawUsage);
    this.uvAttr = new BufferAttribute(this.uv, 2).setUsage(DynamicDrawUsage);
    this.colAttr = new BufferAttribute(this.col, 4).setUsage(DynamicDrawUsage);
    geometry.setAttribute('position', this.posAttr);
    geometry.setAttribute('uv', this.uvAttr);
    geometry.setAttribute('color', this.colAttr);
    geometry.setIndex(new BufferAttribute(index, 1));
    this.material = new MeshBasicMaterial({ map: atlas, vertexColors: true, transparent: true, depthWrite: false, side: DoubleSide, toneMapped: false });
    this.mesh = new Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  get count(): number {
    return this.n;
  }

  begin(): void {
    this.n = 0;
  }

  get full(): boolean {
    return this.n >= this.capacity;
  }

  /** Quad general : centre (x,y,z), demi-axes u et v (vecteurs monde), cellule, couleur rgba. */
  quad(cell: Cell, x: number, y: number, z: number, ux: number, uy: number, uz: number, vx: number, vy: number, vz: number, r: number, g: number, b: number, a: number): void {
    if (this.n >= this.capacity || a <= 0.003) return;
    const i = this.n++;
    const p = i * 12;
    const P = this.pos;
    P[p] = x - ux - vx;
    P[p + 1] = y - uy - vy;
    P[p + 2] = z - uz - vz;
    P[p + 3] = x + ux - vx;
    P[p + 4] = y + uy - vy;
    P[p + 5] = z + uz - vz;
    P[p + 6] = x - ux + vx;
    P[p + 7] = y - uy + vy;
    P[p + 8] = z - uz + vz;
    P[p + 9] = x + ux + vx;
    P[p + 10] = y + uy + vy;
    P[p + 11] = z + uz + vz;
    const cx = cell % COLS;
    const cy = Math.floor(cell / COLS);
    const u0 = cx / COLS;
    const u1 = (cx + 1) / COLS;
    const v1 = 1 - cy / ROWS;
    const v0 = 1 - (cy + 1) / ROWS;
    const U = this.uv;
    const q = i * 8;
    U[q] = u0;
    U[q + 1] = v0;
    U[q + 2] = u1;
    U[q + 3] = v0;
    U[q + 4] = u0;
    U[q + 5] = v1;
    U[q + 6] = u1;
    U[q + 7] = v1;
    const C = this.col;
    const c = i * 16;
    for (let k = 0; k < 4; k++) {
      C[c + k * 4] = r;
      C[c + k * 4 + 1] = g;
      C[c + k * 4 + 2] = b;
      C[c + k * 4 + 3] = a;
    }
  }

  /** Billboard face camera, `size` = demi-cote, `rot` = rotation dans le plan ecran. */
  billboard(cam: CamBasis, cell: Cell, x: number, y: number, z: number, size: number, rot: number, r: number, g: number, b: number, a: number): void {
    const c = Math.cos(rot) * size;
    const s = Math.sin(rot) * size;
    const { right, up } = cam;
    this.quad(cell, x, y, z, right.x * c + up.x * s, right.y * c + up.y * s, right.z * c + up.z * s, up.x * c - right.x * s, up.y * c - right.y * s, up.z * c - right.z * s, r, g, b, a);
  }

  /** Quad etire le long de (dx, dy, dz) (unitaire) : longueur totale 2*half, largeur 2*wid, face camera autour de cet axe. */
  oriented(cam: CamBasis, cell: Cell, x: number, y: number, z: number, dx: number, dy: number, dz: number, half: number, wid: number, r: number, g: number, b: number, a: number): void {
    // axe lateral = dir x (camera -> point)
    const vx0 = x - cam.pos.x;
    const vy0 = y - cam.pos.y;
    const vz0 = z - cam.pos.z;
    let sx = dy * vz0 - dz * vy0;
    let sy = dz * vx0 - dx * vz0;
    let sz = dx * vy0 - dy * vx0;
    const l = Math.hypot(sx, sy, sz) || 1;
    sx = (sx / l) * wid;
    sy = (sy / l) * wid;
    sz = (sz / l) * wid;
    this.quad(cell, x, y, z, dx * half, dy * half, dz * half, sx, sy, sz, r, g, b, a);
  }

  /** Quad pose a plat sur le sol (y fixe), `size` = demi-cote, `rot` = lacet. */
  flat(cell: Cell, x: number, y: number, z: number, size: number, rot: number, r: number, g: number, b: number, a: number): void {
    const c = Math.cos(rot) * size;
    const s = Math.sin(rot) * size;
    this.quad(cell, x, y, z, c, 0, s, -s, 0, c, r, g, b, a);
  }

  end(): void {
    this.mesh.visible = this.n > 0;
    if (this.n === 0) return;
    this.mesh.geometry.setDrawRange(0, this.n * 6);
    this.posAttr.needsUpdate = true;
    this.uvAttr.needsUpdate = true;
    this.colAttr.needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

/** Lot de rubans (traines, eclairs) : une polyligne = une bande face camera, largeur et opacite decroissantes, couleur fixe ou degrade de teinte. */
export class RibbonBatch {
  readonly mesh: Mesh;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly index: Uint32Array;
  private readonly posAttr: BufferAttribute;
  private readonly colAttr: BufferAttribute;
  private readonly indexAttr: BufferAttribute;
  private verts = 0;
  private tris = 0;
  readonly material: MeshBasicMaterial;
  private readonly rgb: number[] = [0, 0, 0];

  constructor(
    private readonly scene: Scene,
    readonly pointCapacity: number,
    renderOrder = 3,
  ) {
    this.pos = new Float32Array(pointCapacity * 6);
    this.col = new Float32Array(pointCapacity * 8);
    this.index = new Uint32Array(pointCapacity * 6);
    const geometry = new BufferGeometry();
    this.posAttr = new BufferAttribute(this.pos, 3).setUsage(DynamicDrawUsage);
    this.colAttr = new BufferAttribute(this.col, 4).setUsage(DynamicDrawUsage);
    this.indexAttr = new BufferAttribute(this.index, 1).setUsage(DynamicDrawUsage);
    geometry.setAttribute('position', this.posAttr);
    geometry.setAttribute('color', this.colAttr);
    geometry.setIndex(this.indexAttr);
    this.material = new MeshBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, side: DoubleSide, toneMapped: false });
    this.mesh = new Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  begin(): void {
    this.verts = 0;
    this.tris = 0;
  }

  get room(): number {
    return this.pointCapacity - this.verts / 2;
  }

  /**
   * Ruban par les `n` points (xs, ys, zs ; indice 0 = tete) : largeur w0 -> w1, opacite a0 -> a1, couleur (r,g,b) ou degrade de teinte
   * (`hue0` + `hueSpan` le long du ruban si `rainbow`).
   */
  strip(cam: CamBasis, n: number, xs: ArrayLike<number>, ys: ArrayLike<number>, zs: ArrayLike<number>, w0: number, w1: number, a0: number, a1: number, r: number, g: number, b: number, rainbow = false, hue0 = 0, hueSpan = 0): void {
    if (n < 2 || this.verts / 2 + n > this.pointCapacity) return;
    const base = this.verts;
    for (let i = 0; i < n; i++) {
      const x = xs[i] as number;
      const y = ys[i] as number;
      const z = zs[i] as number;
      const j = Math.min(n - 1, i + 1);
      const k = Math.max(0, i - 1);
      const tx = (xs[k] as number) - (xs[j] as number);
      const ty = (ys[k] as number) - (ys[j] as number);
      const tz = (zs[k] as number) - (zs[j] as number);
      const vx = x - cam.pos.x;
      const vy = y - cam.pos.y;
      const vz = z - cam.pos.z;
      let sx = ty * vz - tz * vy;
      let sy = tz * vx - tx * vz;
      let sz = tx * vy - ty * vx;
      const l = Math.hypot(sx, sy, sz) || 1;
      const f = i / (n - 1);
      const w = w0 + (w1 - w0) * f;
      sx = (sx / l) * w;
      sy = (sy / l) * w;
      sz = (sz / l) * w;
      const v = (base + i * 2) * 3;
      this.pos[v] = x + sx;
      this.pos[v + 1] = y + sy;
      this.pos[v + 2] = z + sz;
      this.pos[v + 3] = x - sx;
      this.pos[v + 4] = y - sy;
      this.pos[v + 5] = z - sz;
      let cr = r;
      let cg = g;
      let cb = b;
      if (rainbow) {
        hsv(hue0 + hueSpan * f, 0.95, 1, this.rgb);
        cr = this.rgb[0] as number;
        cg = this.rgb[1] as number;
        cb = this.rgb[2] as number;
      }
      const a = a0 + (a1 - a0) * f;
      const c = (base + i * 2) * 4;
      for (let s2 = 0; s2 < 2; s2++) {
        this.col[c + s2 * 4] = cr;
        this.col[c + s2 * 4 + 1] = cg;
        this.col[c + s2 * 4 + 2] = cb;
        this.col[c + s2 * 4 + 3] = a;
      }
    }
    for (let i = 0; i < n - 1; i++) {
      const a = base + i * 2;
      const t = this.tris * 3;
      this.index[t] = a;
      this.index[t + 1] = a + 1;
      this.index[t + 2] = a + 2;
      this.index[t + 3] = a + 2;
      this.index[t + 4] = a + 1;
      this.index[t + 5] = a + 3;
      this.tris += 2;
    }
    this.verts += n * 2;
  }

  end(): void {
    this.mesh.visible = this.tris > 0;
    if (this.tris === 0) return;
    this.mesh.geometry.setDrawRange(0, this.tris * 3);
    this.posAttr.needsUpdate = true;
    this.colAttr.needsUpdate = true;
    this.indexAttr.needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
